<template>
  <app-confirm />

  <w-flex
    :key="activeTab.name"
    fluid
    direction="col"
    class="tab"
    align="stretch"
    :class="activeTab.disabled ? `tab--disabled` : undefined"
  >
    <w-flex tag="header" class="tab__header" :gap="4">
      <h1 v-if="activeTab.title" class="font-h4" v-html="activeTab.title" />

      <w-chip
        v-if="activeTab.disabled"
        icon="lock"
        color="neutral"
        text="Edycja zablokowana"
        class="tab__header__disabled-chip"
      />
    </w-flex>

    <w-flex
      v-if="activeTab.description || activeTab.unlockInfo"
      tag="tab"
      direction="col"
      class="tab__messages"
    >
      <w-message v-if="activeTab.description" color="info" :text="activeTab.description" />

      <w-message
        v-if="activeTab.unlockInfo"
        color="neutral"
        icon="lock"
        :text="activeTab.unlockInfo"
      />
    </w-flex>

    <w-flex class="tab__content" direction="col">
      <slot :name="activeTab.name" />
    </w-flex>

    <w-flex class="tab__footer" align="center" :gap="4">
      <w-button
        text="Wróć"
        icon="chevron_left"
        color="neutral"
        variant="tonal"
        :disabled="isFirstIndex"
        @click="$emit('prev-step')"
      />

      <span class="tab__footer__step-counter">
        Krok {{ currentTabNumber }} z {{ numberOfSteps }}
      </span>

      <w-button
        v-if="!isLastIndex"
        text="Dalej"
        color="primary"
        variant="tonal"
        icon="chevron_right"
        icon-position="right"
        :disabled="isLastIndex"
        @click="$emit('next-step')"
      />

      <w-button
        v-else
        color="primary"
        icon="save"
        text="Zapisz i wyjdź"
        @click="$emit('save', true)"
      />
    </w-flex>
  </w-flex>
</template>

<script setup lang="ts">
import WChip from '@witkac/vue3-components/src/components/w-chip/WChip.vue';
import WMessage from '@witkac/vue3-components/src/components/w-message/WMessage.vue';
import { computed } from 'vue';

import type { Tab } from '../../typings/tab';
import AppConfirm from '../confirm/Confirm.vue';

const props = defineProps<{
  activeTab: Tab;
  block?: object;
  isBlocked?: boolean;
  isLastIndex?: boolean;
  isFirstIndex?: boolean;
  numberOfSteps: number;
  tabs: Tab[];
}>();

defineEmits({
  save: null,
  'prev-step': null,
  'next-step': null,
});

const currentTabNumber = computed(() => {
  return props.tabs.findIndex((tab) => tab.name === props.activeTab.name) + 1;
});
</script>

<style lang="scss">
.tab {
  background: w.color('background', 100);
  border: solid 1px w.color('neutral', 75);
  border-radius: w.radius('small');
  animation: fade-in-top w.transition('regular') w.ease('out-quart');

  &__header,
  &__footer,
  &__messages {
    padding: 12px w.size('tab-padding');
  }

  &__content {
    border-bottom: solid 1px w.color('neutral', 75);
    border-top: solid 1px w.color('neutral', 75);
    padding: w.size('tab-padding') w.size('tab-padding');
  }

  &__footer {
    &__step-counter {
      font-size: w.font('small');
      color: w.color('neutral', 500);
    }
  }

  &__messages {
    border-top: solid 1px w.color('neutral', 75);
  }

  &--disabled {
    .tab {
      &__header {
        color: w.color('disabled', 700);
        background: repeating-linear-gradient(
          45deg,
          rgba(w.color('disabled', 600), 0.1),
          rgba(w.color('disabled', 600), 0.1) 5px,
          rgba(w.color('disabled', 600), 0.2) 5px,
          rgba(w.color('disabled', 600), 0.2) 25px
        );
      }

      &__content,
      &__messages {
        background: rgba(w.color('disabled', 600), 0.1);
      }
    }
  }
}
</style>
